import { normalizeFeedSport, type FeedSport } from './marketFrameNormalization';

export const FEED_VENUES = ['kalshi', 'polymarket', 'polymarket_us'] as const;
export type FeedVenue = typeof FEED_VENUES[number];
export interface FeedPair { sport: FeedSport; venue: FeedVenue }

export function feedPairKey(sport: FeedSport, venue: FeedVenue): string {
  return `${sport}|${venue}`;
}

/** Absence is legacy all-venues; a present malformed/empty list grants nothing. */
export function readFeedPairs(raw: unknown): FeedPair[] | undefined {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw)) return [];
  const pairs = new Map<string, FeedPair>();
  for (const value of raw) {
    if (typeof value !== 'object' || value === null) continue;
    const { sport: code, venue } = value as Record<string, unknown>;
    const sport = typeof code === 'string' ? normalizeFeedSport(code) : null;
    if (!sport || !FEED_VENUES.includes(venue as FeedVenue)) continue;
    const pair = { sport, venue: venue as FeedVenue };
    pairs.set(feedPairKey(sport, pair.venue), pair);
  }
  return [...pairs.values()];
}

/** The eligible named primary owns. Otherwise the sole eligible claimant
 *  (anti-subtraction, per pair: an assignment says who is preferred, never
 *  who may not serve); two or more is ambiguity and nobody owns. With
 *  `primaryOnly` (the `?directoryFollow=off` kill switch) an assigned pair
 *  inspects only its named primary, as it did from b411829c5 to this change. */
export function selectPairOwner<T extends { id: string }>(
  claimants: readonly T[], assigned: ReadonlySet<string> | undefined,
  eligible: (claimant: T) => boolean = () => true, primaryOnly = false,
): T | null {
  if (assigned && assigned.size > 0) {
    // Conflicting assignments are not a client-side election.
    if (assigned.size !== 1) return null;
    const primary = claimants.find(c => assigned.has(c.id) && eligible(c)) ?? null;
    if (primary || primaryOnly) return primary;
  }
  let owner: T | null = null;
  for (const claimant of claimants) {
    if (!eligible(claimant)) continue;
    if (owner !== null) return null;
    owner = claimant;
  }
  return owner;
}
